import {
  BankAccount,
  CreditCard,
  Investment,
  CryptoHolding,
  PrivateEquity,
  HSA,
  Asset,
  Liability,
  Retirement401k,
  ReturnResult,
  BudgetCategory,
  Goal,
  NetWorthSnapshot,
} from '../types';
import { BUDGET_THRESHOLDS } from '../config/constants';

/**
 * Calculate return on investment
 * @param shares - Number of shares owned
 * @param costBasis - Cost per share when purchased
 * @param currentPrice - Current price per share
 * @returns Object with gain amount and percentage
 */
export const calculateReturn = (
  shares: number,
  costBasis: number,
  currentPrice: number
): ReturnResult => {
  if (!shares || !costBasis || !currentPrice || isNaN(shares) || isNaN(costBasis) || isNaN(currentPrice)) {
    return { gain: 0, percentage: 0 };
  }

  const invested = shares * costBasis;
  const current = shares * currentPrice;
  const gain = current - invested;
  const percentage = invested > 0 ? (gain / invested) * 100 : 0;

  return { gain, percentage };
};

/**
 * Dollar gain for one row: current value minus the cost already stored on that row.
 * Returns 0 until both numbers are present.
 */
const rowGain = (value: number, cost: number): number => {
  if (!Number.isFinite(value) || !Number.isFinite(cost) || cost <= 0 || value <= 0) return 0;
  return value - cost;
};

/**
 * Total gain on the Investments tab.
 * Stocks and crypto store cost per unit, so a row's cost is units × that cost.
 * 401(k) and private equity store the total dollars invested on the row.
 */
export const calculatePortfolioGain = (
  investments: Investment[],
  cryptoHoldings: CryptoHolding[],
  privateEquityHoldings: PrivateEquity[] = [],
  retirement401k: Retirement401k[] = [],
): number => {
  const stockGain = investments.reduce((sum, inv) => {
    const cost = (inv.shares || 0) * (inv.costBasis || 0);
    const value = inv.totalValue > 0 ? inv.totalValue : (inv.shares || 0) * (inv.currentPrice || 0);
    return sum + rowGain(value, cost);
  }, 0);

  const cryptoGain = cryptoHoldings.reduce((sum, holding) => {
    const cost = (holding.amount || 0) * (holding.costBasis || 0);
    const value = holding.totalValue > 0 ? holding.totalValue : (holding.amount || 0) * (holding.currentPrice || 0);
    return sum + rowGain(value, cost);
  }, 0);

  const privateEquityGain = privateEquityHoldings.reduce((sum, holding) => {
    return sum + rowGain(holding.totalValue || 0, holding.costBasis || 0);
  }, 0);

  const retirementGain = retirement401k.reduce((sum, holding) => {
    return sum + rowGain(holding.balance || 0, holding.costBasis || 0);
  }, 0);

  return stockGain + cryptoGain + privateEquityGain + retirementGain;
};

/**
 * Calculate total banking balance
 * @param accounts - Array of bank accounts
 * @returns Total balance
 */
export const calculateTotalBanking = (accounts: BankAccount[]): number => {
  return accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
};

/**
 * Calculate total credit card usage
 * @param cards - Array of credit cards
 * @returns Object with total used and total limit
 */
export const calculateTotalCredit = (cards: CreditCard[]): { used: number; limit: number } => {
  return {
    used: cards.reduce((sum, card) => sum + (card.balance || 0), 0),
    limit: cards.reduce((sum, card) => sum + (card.limit || 0), 0),
  };
};

/**
 * Calculate total investment value
 * @param investments - Array of investments
 * @returns Total investment value
 */
export const calculateTotalInvestments = (investments: Investment[]): number => {
  return investments.reduce((sum, inv) => {
    const value = inv.totalValue || 0;
    return sum + (isNaN(value) ? 0 : value);
  }, 0);
};

/**
 * Calculate total 401k balance
 * @param holdings - Array of 401k holdings
 * @returns Total 401k balance
 */
export const calculateTotal401k = (holdings: Retirement401k[]): number => {
  return holdings.reduce((sum, h) => sum + (h.balance || 0), 0);
};

/**
 * Calculate total crypto holdings value
 */
export const calculateTotalCrypto = (holdings: CryptoHolding[]): number => {
  return holdings.reduce((sum, h) => sum + (h.totalValue || 0), 0);
};

/**
 * Calculate total private equity holdings value
 */
export const calculateTotalPrivateEquity = (holdings: PrivateEquity[]): number => {
  return holdings.reduce((sum, h) => sum + (h.totalValue || 0), 0);
};

/**
 * Calculate total HSA balance
 */
export const calculateTotalHSA = (accounts: HSA[]): number => {
  return accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
};

/**
 * Calculate total assets value (including banking)
 * @param assets - Array of assets
 * @param totalBanking - Total banking balance
 * @returns Total assets value
 */
export const calculateTotalAssets = (assets: Asset[], totalBanking: number, totalInvestments: number = 0, total401k: number = 0, totalCrypto: number = 0, totalPrivateEquity: number = 0, totalHSA: number = 0): number => {
  // Exclude "Investment Portfolio" placeholder asset to avoid double-counting with totalInvestments
  const assetSum = assets.reduce((sum, asset) => {
    if (asset.name === 'Investment Portfolio') return sum;
    return sum + (asset.value || 0);
  }, 0);
  return assetSum + totalBanking + totalInvestments + total401k + totalCrypto + totalPrivateEquity + totalHSA;
};

/**
 * Calculate total liabilities
 * @param liabilities - Array of liabilities
 * @returns Total liabilities value
 */
export const calculateTotalLiabilities = (liabilities: Liability[]): number => {
  return liabilities.reduce((sum, liability) => sum + (liability.value || 0), 0);
};

/**
 * Calculate net worth
 * @param totalAssets - Total assets value
 * @param totalLiabilities - Total liabilities value
 * @returns Net worth
 */
export const calculateNetWorth = (totalAssets: number, totalLiabilities: number): number => {
  return totalAssets - totalLiabilities;
};

/**
 * Calculate credit card utilization percentage
 * @param balance - Current balance
 * @param limit - Credit limit
 * @returns Utilization percentage
 */
export const calculateUtilization = (balance: number, limit: number): number => {
  if (!limit || limit === 0) return 0;
  return (balance / limit) * 100;
};

/**
 * Get utilization status color class
 * @param utilization - Utilization percentage
 * @returns Tailwind color class
 */
export const getUtilizationColor = (utilization: number): string => {
  if (utilization > 50) return 'bg-red-500';
  if (utilization > 30) return 'bg-yellow-500';
  return 'bg-green-500';
};

/**
 * Calculate asset distribution percentages
 * @param totalAssets - Total assets value
 * @param totalBanking - Total banking balance
 * @param totalInvestments - Total investment value
 * @returns Object with distribution percentages
 */
export const calculateAssetDistribution = (
  totalAssets: number,
  totalBanking: number,
  totalInvestments: number,
  total401k: number = 0,
  totalCrypto: number = 0,
  totalPrivateEquity: number = 0
): { banking: number; investments: number; retirement: number; crypto: number; privateEquity: number; other: number } => {
  if (totalAssets === 0) {
    return { banking: 0, investments: 0, retirement: 0, crypto: 0, privateEquity: 0, other: 0 };
  }

  return {
    banking: (totalBanking / totalAssets) * 100,
    investments: (totalInvestments / totalAssets) * 100,
    retirement: (total401k / totalAssets) * 100,
    crypto: (totalCrypto / totalAssets) * 100,
    privateEquity: (totalPrivateEquity / totalAssets) * 100,
    other: ((totalAssets - totalBanking - totalInvestments - total401k - totalCrypto - totalPrivateEquity) / totalAssets) * 100,
  };
};

// Budget calculations
export const calculateTotalBudgeted = (categories: BudgetCategory[]): number => {
  return categories.reduce((sum, cat) => sum + (cat.budgeted || 0), 0);
};

export const calculateTotalSpent = (categories: BudgetCategory[]): number => {
  return categories.reduce((sum, cat) => sum + (cat.spent || 0), 0);
};

export const getBudgetColor = (spent: number, budgeted: number): string => {
  if (budgeted <= 0) return 'bg-slate-400';
  const ratio = spent / budgeted;
  if (ratio > BUDGET_THRESHOLDS.yellow) return 'bg-red-500';
  if (ratio > BUDGET_THRESHOLDS.green) return 'bg-yellow-500';
  return 'bg-green-500';
};

// Goal calculations
export const calculateTotalGoalsSaved = (goals: Goal[]): number => {
  return goals.filter(g => g.unit === 'USD').reduce((sum, g) => sum + (g.currentAmount || 0), 0);
};

export const calculateTotalGoalsTarget = (goals: Goal[]): number => {
  return goals.filter(g => g.unit === 'USD').reduce((sum, g) => sum + (g.targetAmount || 0), 0);
};

export const isGoalOnTrack = (goal: Goal): boolean => {
  if (goal.currentAmount >= goal.targetAmount) return true;
  if (!goal.targetDate) return goal.currentAmount > 0;
  const now = new Date();
  const target = new Date(goal.targetDate);
  const start = new Date(now.getFullYear(), 0, 1); // rough start
  const totalDays = Math.max((target.getTime() - start.getTime()) / (1000 * 60 * 60 * 24), 1);
  const elapsedDays = Math.max((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24), 0);
  const expectedProgress = elapsedDays / totalDays;
  const actualProgress = goal.targetAmount > 0 ? goal.currentAmount / goal.targetAmount : 0;
  return actualProgress >= expectedProgress * 0.8;
};

export const getGoalColor = (goal: Goal): string => {
  if (goal.currentAmount >= goal.targetAmount) return 'bg-green-500';
  return isGoalOnTrack(goal) ? 'bg-green-500' : 'bg-yellow-500';
};

// SVG line chart calculations
export const calculateLineChartPoints = (
  snapshots: NetWorthSnapshot[],
  field: keyof NetWorthSnapshot,
  viewBoxWidth: number,
  viewBoxHeight: number,
  padding: number
): string => {
  if (snapshots.length < 2) return '';

  const values = snapshots.map(s => {
    const v = s[field];
    return typeof v === 'number' ? v : 0;
  });

  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;

  const chartWidth = viewBoxWidth - padding * 2;
  const chartHeight = viewBoxHeight - padding * 2;

  return snapshots
    .map((_, i) => {
      const x = padding + (i / (snapshots.length - 1)) * chartWidth;
      const y = padding + chartHeight - ((values[i] - minVal) / range) * chartHeight;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
};

export interface ChartScale {
  minVal: number;
  maxVal: number;
  range: number;
}

export interface ChartPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Simple moving average. Leading values before a full window are null. */
export type ChartInterval = 'day' | 'week' | 'month';

/** Monday of the week containing an ISO date (YYYY-MM-DD), as YYYY-MM-DD. */
const weekStart = (date: string): string => {
  const d = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};

/**
 * One snapshot per week or month: the last one in each period, like a closing
 * price. Expects snapshots sorted by date; 'day' returns them unchanged.
 */
export const resampleSnapshots = <T extends { date: string }>(sorted: T[], interval: ChartInterval): T[] => {
  if (interval === 'day') return sorted;
  const periodOf = interval === 'week' ? weekStart : (date: string) => date.slice(0, 7);
  const out: T[] = [];
  let lastPeriod: string | null = null;
  for (const snap of sorted) {
    const period = periodOf(snap.date);
    if (period === lastPeriod) out[out.length - 1] = snap;
    else out.push(snap);
    lastPeriod = period;
  }
  return out;
};

export const calculateSMA = (values: number[], period: number): (number | null)[] => {
  if (period < 1 || values.length === 0) return values.map(() => null);
  const out: (number | null)[] = new Array(values.length).fill(null);
  let windowSum = 0;
  for (let i = 0; i < values.length; i++) {
    windowSum += values[i];
    if (i >= period) windowSum -= values[i - period];
    if (i >= period - 1) out[i] = windowSum / period;
  }
  return out;
};

/** Exponential moving average. Seeds with the first value once period is reached via SMA. */
export const calculateEMA = (values: number[], period: number): (number | null)[] => {
  if (period < 1 || values.length === 0) return values.map(() => null);
  const out: (number | null)[] = new Array(values.length).fill(null);
  if (values.length < period) return out;

  let sum = 0;
  for (let i = 0; i < period; i++) sum += values[i];
  let ema = sum / period;
  out[period - 1] = ema;

  const k = 2 / (period + 1);
  for (let i = period; i < values.length; i++) {
    ema = values[i] * k + ema * (1 - k);
    out[i] = ema;
  }
  return out;
};

/**
 * Wilder RSI. Leading values before a full window are null.
 * Matches TradingView / Pine `ta.rsi` seeding (SMA of first gains/losses).
 */
export const calculateRSI = (values: number[], period = 14): (number | null)[] => {
  if (period < 1 || values.length === 0) return values.map(() => null);
  const out: (number | null)[] = new Array(values.length).fill(null);
  if (values.length <= period) return out;

  let gainSum = 0;
  let lossSum = 0;
  for (let i = 1; i <= period; i++) {
    const delta = values[i] - values[i - 1];
    if (delta >= 0) gainSum += delta;
    else lossSum -= delta;
  }

  let avgGain = gainSum / period;
  let avgLoss = lossSum / period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  for (let i = period + 1; i < values.length; i++) {
    const delta = values[i] - values[i - 1];
    const gain = delta > 0 ? delta : 0;
    const loss = delta < 0 ? -delta : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }

  return out;
};

/** Shared min/max across one or more series, with a little headroom. */
export const getSharedChartScale = (
  series: Array<Array<number | null | undefined>>,
  options: { padRatio?: number; floorAtZero?: boolean } = {}
): ChartScale => {
  const padRatio = options.padRatio ?? 0.06;
  const floorAtZero = options.floorAtZero ?? false;

  let minVal = Infinity;
  let maxVal = -Infinity;

  for (const values of series) {
    for (const v of values) {
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      if (v < minVal) minVal = v;
      if (v > maxVal) maxVal = v;
    }
  }

  if (!Number.isFinite(minVal) || !Number.isFinite(maxVal)) {
    return { minVal: 0, maxVal: 1, range: 1 };
  }

  // Dollar charts with assets + liabilities should keep $0 on the axis so
  // magnitudes stay honest. Pure trend zoom (no floor) is for single-series.
  if (floorAtZero && minVal >= 0) {
    minVal = 0;
    const pad = Math.max(maxVal * padRatio, 1);
    maxVal += pad;
    return { minVal, maxVal, range: maxVal - minVal || 1 };
  }

  const span = maxVal - minVal;
  const pad = span === 0 ? Math.max(Math.abs(maxVal) * 0.05, 1) : span * padRatio;
  minVal -= pad;
  maxVal += pad;
  return { minVal, maxVal, range: maxVal - minVal || 1 };
};

/** Map a value series onto a shared scale as an SVG polyline points string. */
export const valuesToPolylinePoints = (
  values: Array<number | null | undefined>,
  scale: ChartScale,
  viewBoxWidth: number,
  viewBoxHeight: number,
  padding: ChartPadding
): string => {
  if (values.length < 2) return '';

  const chartWidth = viewBoxWidth - padding.left - padding.right;
  const chartHeight = viewBoxHeight - padding.top - padding.bottom;
  const last = values.length - 1;
  const parts: string[] = [];

  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    const x = padding.left + (i / last) * chartWidth;
    const y = padding.top + chartHeight - ((v - scale.minVal) / scale.range) * chartHeight;
    parts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }

  return parts.length >= 2 ? parts.join(' ') : '';
};

/** Area path under a line (closes down to the chart floor). */
export const valuesToAreaPath = (
  values: Array<number | null | undefined>,
  scale: ChartScale,
  viewBoxWidth: number,
  viewBoxHeight: number,
  padding: ChartPadding
): string => {
  if (values.length < 2) return '';

  const chartWidth = viewBoxWidth - padding.left - padding.right;
  const chartHeight = viewBoxHeight - padding.top - padding.bottom;
  const last = values.length - 1;
  const floorY = padding.top + chartHeight;
  const points: Array<{ x: number; y: number }> = [];

  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    points.push({
      x: padding.left + (i / last) * chartWidth,
      y: padding.top + chartHeight - ((v - scale.minVal) / scale.range) * chartHeight,
    });
  }

  if (points.length < 2) return '';

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const first = points[0];
  const end = points[points.length - 1];
  return `${line} L${end.x.toFixed(1)},${floorY.toFixed(1)} L${first.x.toFixed(1)},${floorY.toFixed(1)} Z`;
};

/**
 * Closed path between two series (MA cloud). Only uses indices where both values exist.
 * Walks series A forward, then series B backward.
 */
export const valuesToCloudPath = (
  seriesA: Array<number | null | undefined>,
  seriesB: Array<number | null | undefined>,
  scale: ChartScale,
  viewBoxWidth: number,
  viewBoxHeight: number,
  padding: ChartPadding
): string => {
  const n = Math.min(seriesA.length, seriesB.length);
  if (n < 2) return '';

  const chartWidth = viewBoxWidth - padding.left - padding.right;
  const chartHeight = viewBoxHeight - padding.top - padding.bottom;
  const last = n - 1;
  const upper: Array<{ x: number; y: number }> = [];
  const lower: Array<{ x: number; y: number }> = [];

  for (let i = 0; i < n; i++) {
    const a = seriesA[i];
    const b = seriesB[i];
    if (typeof a !== 'number' || !Number.isFinite(a)) continue;
    if (typeof b !== 'number' || !Number.isFinite(b)) continue;
    const x = padding.left + (i / last) * chartWidth;
    upper.push({
      x,
      y: padding.top + chartHeight - ((a - scale.minVal) / scale.range) * chartHeight,
    });
    lower.push({
      x,
      y: padding.top + chartHeight - ((b - scale.minVal) / scale.range) * chartHeight,
    });
  }

  if (upper.length < 2) return '';

  const forward = upper.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const back = [...lower].reverse().map(p => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  return `${forward} ${back} Z`;
};

export const valueToY = (
  value: number,
  scale: ChartScale,
  viewBoxHeight: number,
  padding: ChartPadding
): number => {
  const chartHeight = viewBoxHeight - padding.top - padding.bottom;
  return padding.top + chartHeight - ((value - scale.minVal) / scale.range) * chartHeight;
};

export const indexToX = (
  index: number,
  count: number,
  viewBoxWidth: number,
  padding: ChartPadding
): number => {
  if (count <= 1) return padding.left;
  const chartWidth = viewBoxWidth - padding.left - padding.right;
  return padding.left + (index / (count - 1)) * chartWidth;
};
